import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import axios from "axios";
import { API_BASE_URL, API_PREFIX, API_ENDPOINTS } from "../constants/config";
import { stockService } from "../services/apiService";

const API_URL = `${API_BASE_URL}${API_PREFIX}`;
const cardConfig = [
  {
    key: "lowStock",
    title: "Low Stock",
    icon: "fas fa-exclamation-triangle",
    color: "from-orange-500 to-amber-500",
    bg: "bg-orange-50",
    border: "border-orange-200",
    description: "Medicines running low on stock",
  },
  {
    key: "expiryAlerts",
    title: "Expiry Alert",
    icon: "fas fa-calendar-times",
    color: "from-red-500 to-rose-500",
    bg: "bg-red-50",
    border: "border-red-200",
    description: "Medicines expiring soon or expired",
  },
  {
    key: "pendingPayments",
    title: "Payment Pending",
    icon: "fas fa-clock",
    color: "from-yellow-500 to-orange-400",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    description: "Orders with pending payments",
  },
  {
    key: "todaySales",
    title: "Today's Sales",
    icon: "fas fa-chart-line",
    color: "from-green-500 to-teal-500",
    bg: "bg-green-50",
    border: "border-green-200",
    description: "Sales transactions for today",
  },
];

const renderRow = (key, item) => {
  if (key === "lowStock") return (
    <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-orange-100 shadow-sm">
      <div className="flex items-center gap-2">
        <i className="fas fa-pills text-orange-400 text-xs" />
        <span className="text-xs font-medium text-gray-700">{item.name}</span>
      </div>
      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
        {item.qty} left
      </span>
    </div>
  );
  if (key === "expiryAlerts") return (
    <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-red-100 shadow-sm">
      <div className="flex items-center gap-2">
        <i className="fas fa-capsules text-red-400 text-xs" />
        <div>
          <p className="text-xs font-medium text-gray-700">{item.name}</p>
          <p className="text-xs text-gray-400">{item.batchNo}</p>
        </div>
      </div>
      <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
        {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : "-"}
      </span>
    </div>
  );
  if (key === "pendingPayments") return (
    <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-yellow-100 shadow-sm">
      <div className="flex items-center gap-2">
        <i className="fas fa-receipt text-yellow-500 text-xs" />
        <div>
          <p className="text-xs font-medium text-gray-700">{item.billNo}</p>
          <p className="text-xs text-gray-400">{item.customerMobile}</p>
        </div>
      </div>
      <span className="text-xs font-bold text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full border border-yellow-200">
        ₹{item.dueAmount?.toLocaleString("en-IN")}
      </span>
    </div>
  );
  if (key === "todaySales") return (
    <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-green-100 shadow-sm">
      <div className="flex items-center gap-2">
        <i className="fas fa-shopping-bag text-green-500 text-xs" />
        <div>
          <p className="text-xs font-medium text-gray-700">{item.billNo}</p>
          <p className="text-xs text-gray-400">{item.time}</p>
        </div>
      </div>
      <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
        ₹{item.amount?.toLocaleString("en-IN")}
      </span>
    </div>
  );
  return null;
};

const NotificationCard = ({ notif, items, loading, dashSettings }) => {
  const [offset, setOffset] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const ITEM_HEIGHT = 52;

  useEffect(() => {
    if (items.length <= 3) return;
    if (paused) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setOffset(prev => {
        const max = (items.length - 3) * ITEM_HEIGHT;
        return prev >= max ? 0 : prev + 1;
      });
    }, 30);
    return () => clearInterval(intervalRef.current);
  }, [paused, items.length]);

  return (
    <div className={`${notif.bg} border ${notif.border} rounded-2xl shadow-lg overflow-hidden flex flex-col h-full`}>
      <div className={`bg-gradient-to-r ${notif.color} px-5 py-4 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center shadow-inner">
            <i className={`${notif.icon} text-white text-lg`} />
          </div>
          <div>
            <h2 className="text-white font-bold text-base tracking-wide">{notif.title}</h2>
            <p className="text-white text-opacity-75 text-xs">{notif.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && notif.key === "lowStock" && (
            <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-0.5 rounded-full border border-white border-opacity-30">
              &lt;{dashSettings?.lowStockThreshold ?? 10} qty
            </span>
          )}
          {!loading && notif.key === "expiryAlerts" && (
            <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-0.5 rounded-full border border-white border-opacity-30">
              {dashSettings?.expiryAlertDays ?? 30}d window
            </span>
          )}
          {!loading && notif.key === "todaySales" && items.length > 0 && (
            <span className="bg-white bg-opacity-25 text-white text-xs font-bold px-3 py-1 rounded-full shadow border border-white border-opacity-30">
              ₹{items.reduce((s, i) => s + (i.amount || 0), 0).toLocaleString("en-IN")}
            </span>
          )}
          {!loading && notif.key === "pendingPayments" && items.length > 0 && (
            <span className="bg-white bg-opacity-25 text-white text-xs font-bold px-3 py-1 rounded-full shadow border border-white border-opacity-30">
              ₹{items.reduce((s, i) => s + (i.dueAmount || 0), 0).toLocaleString("en-IN")}
            </span>
          )}
          <span className="bg-white bg-opacity-25 text-white text-xs font-bold px-3 py-1 rounded-full shadow border border-white border-opacity-30">
            {loading ? "-" : items.length} alerts
          </span>
          {!loading && items.length > 0 && (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
            </span>
          )}
        </div>
      </div>

      <div className="relative overflow-hidden flex-1 px-4 py-3"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {loading ? (
          <div className="flex flex-col gap-2 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-200 rounded" />
                  <div className="w-24 h-3 bg-gray-200 rounded" />
                </div>
                <div className="w-12 h-4 bg-gray-200 rounded-full" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <i className="fas fa-check-circle text-3xl mb-2 text-green-400" />
            <p className="text-xs font-medium">All clear! No alerts.</p>
          </div>
        ) : (
          <>
            <div className="absolute top-0 left-0 right-0 h-6 z-10 pointer-events-none"
              style={{ background: "linear-gradient(to bottom, rgba(255,255,255,0.8), transparent)" }} />
            <div className="flex flex-col gap-2 transition-none"
              style={{ transform: `translateY(-${offset}px)` }}>
              {items.map((item, idx) => (
                <div key={idx}>{renderRow(notif.key, item)}</div>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-6 z-10 pointer-events-none"
              style={{ background: "linear-gradient(to top, rgba(255,255,255,0.8), transparent)" }} />
            {paused && (
              <div className="absolute top-2 right-2 z-20 bg-black bg-opacity-40 text-white text-xs px-2 py-0.5 rounded-full">
                <i className="fas fa-pause mr-1 text-xs" />paused
              </div>
            )}
          </>
        )}
      </div>

      <div className="px-4 pb-3">
        <div className={`h-1 rounded-full bg-gradient-to-r ${notif.color} opacity-40`} />
      </div>
    </div>
  );
};

const Home = () => {
  const [data, setData] = useState(() => {
    const cached = sessionStorage.getItem("dashboardData");
    return cached ? JSON.parse(cached) : { lowStock: [], expiryAlerts: [], todaySales: [], pendingPayments: [] };
  });
  const [visibleWidgets, setVisibleWidgets] = useState(null);
  const [dashSettings, setDashSettings] = useState({});
  const [loading, setLoading] = useState(() => !sessionStorage.getItem("dashboardData"));

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`${API_URL}${API_ENDPOINTS.DASHBOARD}`);
        const result = res.data || {};
        setData(result);
        sessionStorage.setItem("dashboardData", JSON.stringify(result));
      } catch {
        if (!sessionStorage.getItem("dashboardData")) {
          setData({ lowStock: [], expiryAlerts: [], todaySales: [], pendingPayments: [] });
        }
      } finally { setLoading(false); }
    };
    fetchDashboard();

    stockService.getDashboardWidgetConfig().then((widgets) => {
      const visMap = {};
      (widgets || []).forEach((w) => { visMap[w.widgetKey] = w.visible !== false; });
      setVisibleWidgets(visMap);
    }).catch(() => setVisibleWidgets(null));

    stockService.getDashboardSettings().then((settings) => {
      const map = {};
      (settings || []).forEach((s) => { map[s.settingKey] = s.settingValue; });
      setDashSettings(map);
    }).catch(() => {});
  }, []);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      <div className="mt-3 mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className="fas fa-th-large text-blue-600" />
          <span className="text-sm font-semibold text-blue-700">Overview</span>
          <span className="text-xs text-gray-400">· Live notifications & alerts</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          Live
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 flex-1 min-h-0">
        {cardConfig.filter((notif) => visibleWidgets === null || visibleWidgets[notif.key] !== false).map(notif => (
          <NotificationCard
            key={notif.key}
            notif={notif}
            items={data[notif.key] || []}
            loading={loading}
            dashSettings={dashSettings}
          />
        ))}
      </div>
    </div>
  );
};

export default Home;
